import { HttpClient } from '@packages/core/http';
import { Account, RetailerResponseDto, TimeZoneResponseDto } from '@packages/models/api';

interface GetAccountsQueryParameters {
    testAccounts?: boolean;
    size?: number;
    page?: number;
    query?: string;
}

interface GetAdminAccountsQueryParameters {
    testAccounts?: boolean;
    size?: number;
    page?: number;
    fieldToSearch: string;
    query?: string;
    includeDisabled: boolean;
    sortField?: string;
    sortDirection?: string;
}
interface GetAccountsByRetailerQueryParameters {
    retailerCode: string;
    text: string;
}
export interface GetAccountsByRetailerResponseBody {
    accountId: string;
    accountSourceId: string;
    accountStatusId: string;
    defaultEmailAddressId: string;
    defaultPhoneNumberId: string;
    soaUserId: string;
    soaUsername: string;
    firstName: string;
    lastName: string;
    timeZone: string;
    locale: string;
    enabled: boolean;
    emailNotificationsEnabled: boolean;
    pushNotificationsEnabled: boolean;
    lastSyncedTimestamp: string;
    createdTimestamp: string;
    createdAccountId: string;
    lastUpdatedTimestamp: string;
    lastUpdatedAccountId: string;
    jobTitle: string;
}

interface GetAccountsResponseBody {
    totalCount: number;
    accounts: Account[];
}

interface UpdateAccountRequestBody {
    addOnRoleIds?: string[];
    accountRoles?: {
        accountRoleId: string;
        roleId: string;
    }[];
    roleTargets?: {
        roleTargetId: string;
        roleId: string;
    }[];
    defaultSavedSearchId?: string | null;
    firstName?: string;
    lastName?: string;
    emailAddress?: string;
    jobTitle?: string;
}

interface UpdateAccountResponseBody {
    account: Account;
}

interface CreateAccountRequestBody {
    soaUsername: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
    roleTargetTypeId?: string;
    baseRole: string;
    addOnRole: string[];
    jobTitle?: string;
}

interface GetUserInfoCardDetailsParameter {
    userName?: string;
    accountId?: string;
}

interface GetRegionZoneDistrictMapQueryParameters {
    regionId?: string;
    zoneId?: string;
    districtId?: string;
    accountId?: string;
}

export interface RegionZoneDistrictMapping {
    regionId: string;
    regionCode: string;
    zoneId: string;
    zoneCode: string;
    districtId: string;
    districtCode: string;
    accountRoleId: string | null;
    accountId: string | null;
    soaUsername: string | null;
    firstName: string | null;
    lastName: string | null;
}

export interface ReportType {
    reportType: string;
    description: string;
}

export interface ReportListItem {
    firstName: String;
    lastName: String;
    reportName: String;
    reportId: String;
    endDate: String;
    requestedDate: String;
    startDate: String;
    status: String;
    username: String;
}

export interface ReportListResponseBody {
    total: number;
    size: number;
    page: number;
    reports: ReportListItem[];
}
interface GetRegionZoneDistrictMapResponseBody {
    regionZoneDistrictMap: RegionZoneDistrictMapping[];
}

export interface RsqmDsqmUser {
    accountId: string;
    soaUsername: string | null;
    firstName: string | null;
    lastName: string | null;
}

export interface GetRsqmDsqmUsersResponseBody {
    rsqmDsqmUsers: RsqmDsqmUser[];
}

export interface SoaUser {
    soaUsername: string;
    firstName: string;
    lastName: string;
    accountId: string;
}

export interface GetSoaUsernamesResponseBody {
    soaUsernames: SoaUser[];
}

export interface UpdateDistrictAssignmentRequestBody {
    existingAccountRoleId: string | null;
    newAccountId: string | null;
    districtId: string;
}

interface UpdateDistrictAssignmentResponseBody {
    districtAssignment: RegionZoneDistrictMapping[];
}

interface GetReportTypesResponseBody {
    reportTypes: ReportType[];
}

interface SubmitReportTypeRequestBody {
    startDate: string;
    endDate: string;
}

export class AccountsService {
    constructor(private http: HttpClient) {}

    getAccounts(queryParameters?: GetAccountsQueryParameters) {
        return this.http.orchestrateRequest<GetAccountsResponseBody>({
            method: 'GET',
            url: `/accounts${this.http.queryString(queryParameters || {})}`,
        });
    }

    getAdminAccounts(queryParameters?: GetAdminAccountsQueryParameters, options?: { signal?: AbortSignal }) {
        return this.http.orchestrateRequest<GetAccountsResponseBody>({
            method: 'GET',
            url: `/admin/accounts${this.http.queryString(queryParameters || {})}`,
            ...options,
        });
    }

    getRsqmDsqmUsers(options?: { signal?: AbortSignal }) {
        return this.http.orchestrateRequest<GetRsqmDsqmUsersResponseBody>({
            method: 'GET',
            url: '/accounts/rsqmDsqmUsers',
            ...options,
        });
    }

    getSoaUser(options?: { signal?: AbortSignal }) {
        return this.http.orchestrateRequest<GetSoaUsernamesResponseBody>({
            method: 'GET',
            url: '/reports/soausernames',
            ...options,
        });
    }

    getAccountsByRetailerNumber(queryParameters: GetAccountsByRetailerQueryParameters) {
        return this.http.orchestrateRequest<GetAccountsByRetailerResponseBody[]>({
            method: 'GET',
            url: `/accounts/findUsers${this.http.queryString(queryParameters || {})}`,
            ignoreCache: true,
        });
    }

    getTimeZones(options?: { ignoreCache?: boolean; signal?: AbortSignal }) {
        return this.http.orchestrateRequest<TimeZoneResponseDto>({
            method: 'GET',
            url: '/accounts/time-zones',
            ...options,
        });
    }

    updateAccount(accountId: string, body: UpdateAccountRequestBody) {
        return this.http.orchestrateRequest<UpdateAccountResponseBody>({
            method: 'PATCH',
            url: `/admin/accounts/${accountId}`,
            body,
        });
    }

    createAccount(createAccountRequest: CreateAccountRequestBody) {
        return this.http.orchestrateRequest<any>({
            method: 'POST',
            url: '/account',
            body: createAccountRequest,
        });
    }

    getUserInfoCardDetails(data: GetUserInfoCardDetailsParameter) {
        return this.http.orchestrateRequest<{ account: Account }>({
            method: 'GET',
            url: `/userInfoCardsAccount${this.http.queryString(data)}`,
        });
    }

    getRegionZoneDistrictMap(queryParameters?: GetRegionZoneDistrictMapQueryParameters) {
        return this.http.orchestrateRequest<GetRegionZoneDistrictMapResponseBody>({
            method: 'GET',
            url: `/accounts/regionZoneDistrictMap${this.http.queryString(queryParameters || {})}`,
        });
    }

    updateDistrictAssignment(body: UpdateDistrictAssignmentRequestBody) {
        return this.http.orchestrateRequest<UpdateDistrictAssignmentResponseBody>({
            method: 'PATCH',
            url: '/accounts/districtAssignment',
            body,
        });
    }

    getReportTypes() {
        return this.http.orchestrateRequest<GetReportTypesResponseBody>({
            method: 'GET',
            url: `/reports/types`,
        });
    }

    async submitReportsData(reportType: string, submitReportTypeBody: SubmitReportTypeRequestBody) {
        return await this.http.orchestrateRequest<any>({
            method: 'POST',
            url: `/reports/${reportType}`,
            body: submitReportTypeBody,
        });
    }

    getReportList(page?: number, size?: number, soaUserName: string | null = null, reportName: string | null = null) {
        // Build query parameters
        const params = new URLSearchParams();
        params.append('page', String(page || 1));
        params.append('size', String(size || 10));

        if (soaUserName) {
            params.append('soaUsername', soaUserName);
        }

        if (reportName) {
            params.append('reportName', reportName);
        }

        return this.http.orchestrateRequest<ReportListResponseBody>({
            method: 'GET',
            url: `/reports/list?${params.toString()}`,
            ignoreCache: true,
        });
    }
}
